import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { useLang } from "@/i18n";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verified, setVerified] = useState(false);
  const inputRefs = useRef([]);

  // OTP preview from registration (only shown when email service is unavailable)
  const otpPreview = location.state?.otpPreview;
  const emailSent = location.state?.emailSent;
  const userEmail = location.state?.email;

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error(t('enterFullCode'));
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/verify-email", { otp: code });
      setVerified(true);
      toast.success(t('emailVerified'));
      setTimeout(() => {
        navigate("/onboarding", { replace: true });
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalidCode'));
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      const res = await api.post("/auth/resend-otp");
      setResendCooldown(60);
      if (res.data.email_sent) {
        toast.success("Nuovo codice inviato alla tua email!");
      } else if (res.data.otp_preview) {
        toast.info(`Codice test: ${res.data.otp_preview}`, { duration: 10000 });
      } else {
        toast.success(t('newCodeSent'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sendError'));
    }
  };

  // Auto-submit when all 6 digits entered
  useEffect(() => {
    if (otp.every(d => d !== "") && !loading && !verified) {
      handleVerify();
    }
  }, [otp]);

  if (verified) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-white via-green-50/30 to-emerald-50/30 px-6">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6 animate-bounce">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('emailVerified')}</h2>
        <p className="text-gray-500">{t('redirecting')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white via-orange-50/30 to-pink-50/30" data-testid="verify-email-page">
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="max-w-sm w-full">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-100 to-pink-100 flex items-center justify-center">
              <Mail className="w-10 h-10 text-orange-500" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Verifica la tua email
          </h1>
          <p className="text-center text-gray-500 text-sm mb-8">
            {userEmail ? (
              <>Inserisci il codice a 6 cifre inviato a <span className="font-medium text-gray-700">{userEmail}</span></>
            ) : (
              "Inserisci il codice a 6 cifre per verificare la tua email"
            )}
          </p>

          {/* Test mode OTP preview - only shown when email service is unavailable */}
          {otpPreview && (
            <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="otp-preview-box">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Modalita test</p>
                  <p className="text-xs text-amber-600 mt-1">
                    Servizio email non disponibile. Usa il codice qui sotto:
                  </p>
                  <p className="text-2xl font-mono font-bold text-amber-900 mt-2 tracking-widest" data-testid="otp-preview-code">
                    {otpPreview}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email sent confirmation */}
          {emailSent && !otpPreview && (
            <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200" data-testid="email-sent-box">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">Email inviata!</p>
                  <p className="text-xs text-green-600 mt-1">
                    Controlla la tua casella di posta{userEmail ? ` (${userEmail})` : ''} e inserisci il codice ricevuto.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* OTP Input */}
          <div className="flex gap-3 justify-center mb-8" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 border-gray-200 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                data-testid={`otp-input-${i}`}
              />
            ))}
          </div>

          {/* Verify Button */}
          <Button
            onClick={handleVerify}
            disabled={loading || otp.some(d => d === "")}
            className="w-full h-14 rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white text-lg font-semibold hover:opacity-90 shadow-xl shadow-pink-500/25 transition-all disabled:opacity-50"
            data-testid="verify-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('verifying')}
              </span>
            ) : (
              t('verifyEmail')
            )}
          </Button>

          {/* Resend */}
          <div className="text-center mt-6">
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="text-sm text-gray-500 hover:text-pink-500 transition-colors disabled:text-gray-300 flex items-center gap-2 mx-auto"
              data-testid="resend-otp-btn"
            >
              <RefreshCw className={`w-4 h-4 ${resendCooldown > 0 ? '' : 'hover:rotate-180 transition-transform'}`} />
              {resendCooldown > 0 ? `${t('resendIn')} ${resendCooldown}s` : t('didntReceiveCode')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
